/**
 * Google Search Console integration API client.
 *
 * Backed by /api/intel/gsc/* on the backend. Two flows:
 *
 *   1. OAuth dance:
 *        - getGscAuthUrl()        → backend mints a signed Google
 *                                    consent URL; SPA opens it in a
 *                                    popup window
 *        - completeGscOauth()     → SPA posts (code, state) it
 *                                    received in the popup back to the
 *                                    backend, which exchanges for
 *                                    refresh_token + persists
 *
 *   2. Connection management + import:
 *        - getGscStatus()         → connected?, property?, last sync?
 *        - listGscProperties()    → live call to Google for the
 *                                    property dropdown
 *        - setGscProperty()       → persist the dropdown pick
 *        - disconnectGsc()        → revoke + delete the row
 *        - previewGscImport()     → see the top queries WITHOUT writing
 *        - importGscKeywords()    → write top queries into seo_keyword
 */

import { getClient } from './base/api';

const client = getClient();

// ---------------- OAuth ----------------

/**
 * Returns { url, redirect_uri, scopes_requested } — the SPA opens
 * `url` in a popup. Optional `redirect_uri` lets us override (for
 * local dev: localhost:3000) but should normally be omitted so the
 * backend uses the production URL.
 */
export const getGscAuthUrl = async (opts = {}) => {
  const params = {};
  if (opts.redirect_uri) params.redirect_uri = opts.redirect_uri;
  return client.get('intel/gsc/auth-url', { params });
};

/**
 * Exchange the authorization code Google handed the popup back to us.
 * Called by the popup itself (the /auth/gsc/callback React route).
 */
export const completeGscOauth = async ({ code, state, redirect_uri }) => {
  return client.post('intel/gsc/callback', {
    code, state, redirect_uri: redirect_uri || undefined,
  });
};

// ---------------- Connection ----------------

export const getGscStatus = async () =>
  client.get('intel/gsc/status');

export const listGscProperties = async () =>
  client.get('intel/gsc/properties');

export const setGscProperty = async (propertyUrl) =>
  client.post('intel/gsc/property', { property_url: propertyUrl });

export const disconnectGsc = async () =>
  client.delete('intel/gsc/connection');

// ---------------- Import / preview ----------------

/**
 * @param {object} opts
 * @param {number} [opts.days=28]      Lookback window in days.
 * @param {number} [opts.top_n=50]     Number of queries to return.
 * @param {string} [opts.sort_by='impressions']  impressions | clicks | ctr | position
 */
export const previewGscImport = async ({
  days = 28, top_n = 50, sort_by = 'impressions',
} = {}) => {
  return client.get('intel/gsc/preview', {
    params: { days, top_n, sort_by },
  });
};

export const importGscKeywords = async ({
  days = 28, top_n = 50, sort_by = 'impressions',
} = {}) => {
  return client.post('intel/gsc/import', { days, top_n, sort_by });
};

const gscApi = {
  getGscAuthUrl, completeGscOauth,
  getGscStatus, listGscProperties, setGscProperty, disconnectGsc,
  previewGscImport, importGscKeywords,
};

export default gscApi;
